import { View } from "react-native";
import React, { useMemo } from "react";
import { UserFields } from "features/types";
import { Text } from "@ui-kitten/components";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import PlanNameWithPaymentInterval from "screens/Account/components/PlanNameWithPaymentInterval";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import Spacer from "components/Spacer";
import Button from "components/Button";
import { useAppDispatch } from "hooks/useAppDispatch";
import { setStep } from "features/slices/buyFlowSlice";
import { useAppSelector } from "hooks/useAppSelector";
import BuyFlowPaymentDetailCard from "components/BuyFlowPaymentDetailCard";
import { useRoute } from "@react-navigation/native";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import { RenderUserFields } from "components/NewMemberForm";
import { Formik } from "formik";
import * as yup from "yup";
import AgreementText from "components/AgreementText";
import OrderSummary from "components/OrderSummary";
import { getPlanType } from "utils/planPackageUtils";
import { getUserName } from "utils/userUtils";

interface StepThreeProps {
  onSubmit: (values?: UserFields) => void;
}
const StepThree: React.FC<StepThreeProps> = ({ onSubmit }) => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const { step, signupCharge, selectedPlanPackage, selectedBillingInterval } =
    useAppSelector((state) => state.buyFlow);
  const { newMembers } = useAppSelector((state) => state.addMembershipFlow);

  const {
    planPackageUserFields,
    planPackageFieldsValidation,
    fieldRefs: planPackageUserFieldsRefs,
  } = usePlanPackageUserFields(selectedPlanPackage?.id);
  const {
    signupCharge: _signupCharge,
    recurringCharge,
    remainingMonths,
  } = signupCharge!;
  const totalCharge =
    _signupCharge + parseFloat(recurringCharge) * remainingMonths;

  // AddSubscriptionToAccountFlow: adding subscription to current member
  // AddMembershipFlow: adding new members to account
  const isAddSubscriptionToAccountFlow = route.name === "AddNewPlan";

  const btnText = useMemo(() => {
    return getPlanType(selectedPlanPackage!) === "RESERVATION"
      ? "AGREE & SUBSCRIBE"
      : "AGREE & PURCHASE";
  }, [selectedPlanPackage]);

  const handleSubmit = (values: UserFields) => {
    onSubmit(values);
  };
  return (
    <Formik
      validationSchema={
        isAddSubscriptionToAccountFlow
          ? yup.object(planPackageFieldsValidation)
          : undefined
      }
      initialValues={{}}
      onSubmit={handleSubmit}
    >
      {({ isValid, handleSubmit, errors }) => (
        <View>
          {newMembers.map((member, index) => (
            <BaseCardListItem
              showArrow={false}
              key={member.phone + index}
              title={
                <View>
                  <Text category="h6">{getUserName(member)}</Text>
                  <PlanNameWithPaymentInterval
                    planPackage={selectedPlanPackage!}
                    billingInterval={selectedBillingInterval}
                    monthlyPrice={selectedPlanPackage!.monthlyPrice}
                  />
                </View>
              }
            />
          ))}

          <Spacer />

          {/* Collect plan package fields in the add plan to existing member flow */}
          {isAddSubscriptionToAccountFlow && (
            <View>
              <Spacer />
              {planPackageUserFields?.map((planPackageUserField, i) => {
                const {
                  id,
                  field: { name, type },
                } = planPackageUserField;

                return (
                  <RenderUserFields
                    key={id}
                    name={name}
                    type={type}
                    userField={planPackageUserField}
                    userFieldRefs={planPackageUserFieldsRefs}
                    index={i}
                  />
                );
              })}
            </View>
          )}

          <BuyFlowPaymentDetailCard
            planPackage={selectedPlanPackage!}
            signupCharge={signupCharge!}
          />

          <Spacer />

          <PaymentMethodCard />

          <Spacer />

          <BaseCard>
            {signupCharge && (
              <OrderSummary
                signupCharge={signupCharge}
                planPackage={selectedPlanPackage!}
              />
            )}
          </BaseCard>

          <Text category="c2" style={{ marginTop: 20 }}>
            By selecting "{btnText}", you agree to start your subscription
            immediately, and that you will not be able to withdraw from the
            contract and receive a refund for monthly or yearly subscriptions
            upon performance of the membership benefits. We will send you a
            renewal notice 30 days prior to yearly renewals becoming effective.
            We will charge the standard monthly or annual fee to your stored
            payment method on a recurring basis. You can cancel at any time,
            effective at the end of the billing period.
          </Text>

          <Spacer>
            <AgreementText
              btnText={btnText}
              textProps={{
                category: "c2",
              }}
            />
          </Spacer>

          <ConfirmPurchaseButton
            onPress={handleSubmit}
            buttonText={btnText}
            charge={parseFloat(totalCharge)}
            disabled={!isValid}
          ></ConfirmPurchaseButton>
          <Button
            appearance="ghost"
            style={{ marginTop: 15 }}
            onPress={() => dispatch(setStep(step - 1))}
          >
            BACK
          </Button>
        </View>
      )}
    </Formik>
  );
};

export default StepThree;
